// ============================================================================
// STORE.COMMUNICATIONS.JS — Referral Pings (internal attention system)
//
// A ping is a referral-bound internal signal telling another user what action
// is needed on a referral. It is NOT chat, email, fax, or document transport.
//
//   Gate      = decision system
//   Workflow  = movement system
//   Pings     = attention system
//   Referral  = truth container  (referralId === threadId)
//
// Canonical path: tenants/{tenantId}/referrals/{referralId}/communications/{communicationId}
//
// Invariants:
//   • threadId === referralId; communicationId === doc id.
//   • status starts OPEN; senderUid === current user.
//   • NO PHI. Strict whitelist builder — unknown keys never persisted.
//   • actionNeeded is enum-derived canned text (not uncontrolled free text).
//   • Optional message is short, length-capped, and PHI-scanned before write.
//   • A ping never moves workflow state and never carries documents.
// ============================================================================

import { getApp } from 'firebase/app';
import {
  getFirestore, doc, setDoc, updateDoc, serverTimestamp,
  collection, collectionGroup, query, where, orderBy, getDocs, limit, onSnapshot,
} from 'firebase/firestore';
import { PATHS } from './firestorePaths.js';
import { scanForPhi, hasBlockingReasons } from '../utils/phiScan.js';

function getDb() {
  return getFirestore(getApp());
}

function generateCommunicationId() {
  return `com_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function generateMessageId() {
  return `msg_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

// ── Enums ─────────────────────────────────────────────────────────────────────

export const COMMUNICATION_PURPOSE = {
  MISSING_INFO:          'MISSING_INFO',
  MISSING_ATTACHMENT:    'MISSING_ATTACHMENT',
  TRIAGE_CLARIFICATION:  'TRIAGE_CLARIFICATION',
  BOOKING_BLOCKER:       'BOOKING_BLOCKER',
  RETURN_TO_INTAKE:      'RETURN_TO_INTAKE',
  CLOSED_LOOP_AVAILABLE: 'CLOSED_LOOP_AVAILABLE',
  CONSULT_NOTE_NEEDED:   'CONSULT_NOTE_NEEDED',
  SCHEDULED_APPOINTMENT_REVIEW: 'SCHEDULED_APPOINTMENT_REVIEW',
};

export const COMMUNICATION_STATUS = {
  OPEN:         'OPEN',
  ACKNOWLEDGED: 'ACKNOWLEDGED',
  RESOLVED:     'RESOLVED',
  CANCELLED:    'CANCELLED',
};

export const RELATED_OBJECT_TYPE = {
  REWORK_REQUEST: 'reworkRequest',
  WORKFLOW_STATE: 'workflowState',
  REFERRAL:       'referral',
};

// Reply (message) types. Only USER_REPLY is client-writable in v1.
export const MESSAGE_TYPE = {
  USER_REPLY:   'USER_REPLY',
  SYSTEM_EVENT: 'SYSTEM_EVENT', // reserved; not written by clients in v1
};

// UX guidance: which receiver roles are most relevant per purpose. This is a
// display filter for the composer's receiver dropdown — NOT the security boundary.
// The hard rule (same-tenant, active, Referral-Pings-enabled, not self) is enforced
// in Firestore rules. If a filter yields no candidates, the UI falls back to all
// enabled roles so the composer is never unsendable.
export const PURPOSE_RECEIVER_ROLES = {
  MISSING_INFO:          ['gp_employee', 'intake_coordinator'],
  MISSING_ATTACHMENT:    ['gp_employee', 'intake_coordinator'],
  TRIAGE_CLARIFICATION:  ['triage_nurse', 'intake_coordinator', 'gp_employee'],
  BOOKING_BLOCKER:       ['scheduler', 'intake_coordinator'],
  RETURN_TO_INTAKE:      ['intake_coordinator'],
  CLOSED_LOOP_AVAILABLE: ['gp_employee', 'specialist_admin'],
  CONSULT_NOTE_NEEDED:   ['specialist'],
  SCHEDULED_APPOINTMENT_REVIEW: ['specialist'],
};

// Canned, non-PHI action text per purpose. actionNeeded is derived from this map
// (never free user input) so the required signal can't carry PHI.
export const COMMUNICATION_ACTION_TEXT = {
  MISSING_INFO:          'Required information is missing. Open the referral and complete the missing item.',
  MISSING_ATTACHMENT:    'A required document is missing. Open the referral and attach it there.',
  TRIAGE_CLARIFICATION:  'Triage needs an answer before review can continue. Open the referral and respond to the requested item.',
  BOOKING_BLOCKER:       'Scheduling cannot continue until this blocker is cleared. Open the referral and resolve the requested item.',
  RETURN_TO_INTAKE:      'This referral was returned to Intake. Open the referral and resolve the scheduling blocker.',
  CLOSED_LOOP_AVAILABLE: 'The closed-loop summary is available. Open the referral to review it.',
  CONSULT_NOTE_NEEDED:   'The consult note is needed before this referral can be closed. Open the referral and upload it there.',
  SCHEDULED_APPOINTMENT_REVIEW: 'A scheduled appointment needs your attention. Open the referral and review the appointment context.',
};

export const COMMUNICATION_PURPOSE_LABELS = {
  MISSING_INFO:          'Add Missing Information',
  MISSING_ATTACHMENT:    'Attach Required Document',
  TRIAGE_CLARIFICATION:  'Answer Triage Question',
  BOOKING_BLOCKER:       'Clear Scheduling Blocker',
  RETURN_TO_INTAKE:      'Fix Returned Intake Item',
  CLOSED_LOOP_AVAILABLE: 'Review Closed-Loop Summary',
  CONSULT_NOTE_NEEDED:   'Add Consult Note',
  SCHEDULED_APPOINTMENT_REVIEW: 'Review Scheduled Appointment',
};

const MESSAGE_MAX_LENGTH = 280;        // optional note on the parent ping
export const MESSAGE_REPLY_MAX_LENGTH = 500; // reply body in the message subcollection

// PHI block message shared by ping notes and replies.
export const PHI_BLOCK_MESSAGE =
  'This reply may contain PHI. Keep Referral Pings non-PHI and complete clinical/document work on the referral.';

// The ONLY keys allowed in a communication MESSAGE (reply) document. Mirrors the
// Firestore rule's allowed-key list. Anything else (incl. PHI) is never written.
const MESSAGE_ALLOWED_KEYS = [
  'tenantId', 'referralId', 'communicationId', 'messageId',
  'authorUid', 'authorRole', 'messageType', 'body', 'createdAt',
];

// The ONLY keys allowed in a communication document. Must mirror the Firestore
// rule's allowed-key list. Anything else (incl. PHI) is never written.
const ALLOWED_KEYS = [
  'tenantId', 'referralId', 'threadId', 'communicationId',
  'purpose', 'actionNeeded', 'message',
  'relatedObjectType', 'relatedObjectId',
  'senderUid', 'senderRole', 'recipientUid', 'recipientRole',
  'status',
  'createdAt',
  'acknowledgedAt', 'acknowledgedByUid',
  'resolvedAt', 'resolvedByUid',
  'cancelledAt', 'cancelledByUid',
  // Unread / activity (non-PHI). lastMessage* maintained by Cloud Function after
  // create; *LastReadAt written only by the owning participant. No preview/body.
  'lastMessageAt', 'lastMessageByUid', 'lastMessageType',
  'senderLastReadAt', 'recipientLastReadAt',
];

// Activity types for lastMessageType (non-PHI). PING_CREATED is the initial value.
export const LAST_MESSAGE_TYPE = {
  PING_CREATED: 'PING_CREATED',
  USER_REPLY:   'USER_REPLY',
  SYSTEM_EVENT: 'SYSTEM_EVENT',
};

// ── Builder (strict whitelist, no PHI) ────────────────────────────────────────

/**
 * Build a communication (ping) document. Throws on invalid input or detected PHI.
 * Returns ONLY whitelisted keys; actionNeeded is canned text from the purpose.
 */
export function buildCommunicationDoc({
  tenantId,
  referralId,
  communicationId,
  purpose,
  message = null,
  relatedObjectType = null,
  relatedObjectId = null,
  senderUid,
  senderRole,
  recipientUid,
  recipientRole,
  now,
}) {
  if (!tenantId) throw new Error('buildCommunicationDoc: tenantId is required');
  if (!referralId) throw new Error('buildCommunicationDoc: referralId is required');
  if (!communicationId) throw new Error('buildCommunicationDoc: communicationId is required');
  if (!COMMUNICATION_PURPOSE[purpose]) throw new Error(`buildCommunicationDoc: invalid purpose "${purpose}"`);
  if (!senderUid) throw new Error('buildCommunicationDoc: senderUid is required');
  if (!senderRole) throw new Error('buildCommunicationDoc: senderRole is required');
  if (!recipientUid) throw new Error('buildCommunicationDoc: recipientUid is required');
  if (!recipientRole) throw new Error('buildCommunicationDoc: recipientRole is required');

  if (relatedObjectType && !Object.values(RELATED_OBJECT_TYPE).includes(relatedObjectType)) {
    throw new Error(`buildCommunicationDoc: invalid relatedObjectType "${relatedObjectType}"`);
  }
  if (relatedObjectType === RELATED_OBJECT_TYPE.REWORK_REQUEST && !relatedObjectId) {
    throw new Error('buildCommunicationDoc: relatedObjectId is required when relatedObjectType is reworkRequest');
  }

  // Optional message: length-capped + PHI-scanned. Block on PHI.
  let safeMessage = null;
  if (message != null && String(message).trim() !== '') {
    const trimmed = String(message).trim();
    if (trimmed.length > MESSAGE_MAX_LENGTH) {
      throw new Error(`Message is too long (max ${MESSAGE_MAX_LENGTH} characters).`);
    }
    const scan = scanForPhi(trimmed);
    if (scan.detected && hasBlockingReasons(scan.reasons)) {
      throw new Error('Message may contain patient information (PHI) and was blocked. Remove identifiers and try again.');
    }
    safeMessage = trimmed;
  }

  const docData = {
    tenantId,
    referralId,
    threadId: referralId,            // invariant: thread === referral
    communicationId,
    purpose,
    actionNeeded: COMMUNICATION_ACTION_TEXT[purpose],
    message: safeMessage,
    relatedObjectType: relatedObjectType ?? null,
    relatedObjectId: relatedObjectId ?? null,
    senderUid,
    senderRole,
    recipientUid,
    recipientRole,
    status: COMMUNICATION_STATUS.OPEN,
    createdAt: now,
    acknowledgedAt: null,
    acknowledgedByUid: null,
    resolvedAt: null,
    resolvedByUid: null,
    cancelledAt: null,
    cancelledByUid: null,
    // Unread / activity tracking (non-PHI). Sender authored the ping → read for sender.
    lastMessageAt:       now,
    lastMessageByUid:    senderUid,
    lastMessageType:     LAST_MESSAGE_TYPE.PING_CREATED,
    senderLastReadAt:    now,
    recipientLastReadAt: null,
  };

  // Defensive: strip any key not in the whitelist (cannot happen here, but
  // guarantees PHI/unknown fields are never persisted).
  for (const k of Object.keys(docData)) {
    if (!ALLOWED_KEYS.includes(k)) delete docData[k];
  }
  return docData;
}

// ── Writes ────────────────────────────────────────────────────────────────────

/**
 * Create a referral-bound ping (single atomic doc write).
 * @returns {Promise<string>} the created communicationId
 */
export async function createCommunicationPing(tenantId, referralId, params) {
  if (!tenantId || !referralId) throw new Error('createCommunicationPing: tenantId and referralId are required');
  const db = getDb();
  const communicationId = generateCommunicationId();
  const docData = buildCommunicationDoc({
    ...params,
    tenantId,
    referralId,
    communicationId,
    now: serverTimestamp(),
  });
  await setDoc(doc(db, PATHS.communication(tenantId, referralId, communicationId)), docData);
  return communicationId;
}

/** Recipient acknowledges an OPEN ping. */
export async function acknowledgeCommunicationPing(tenantId, referralId, communicationId, uid) {
  if (!uid) throw new Error('acknowledgeCommunicationPing: uid is required');
  const db = getDb();
  await updateDoc(doc(db, PATHS.communication(tenantId, referralId, communicationId)), {
    status: COMMUNICATION_STATUS.ACKNOWLEDGED,
    acknowledgedAt: serverTimestamp(),
    acknowledgedByUid: uid,
  });
}

/** Sender cancels an OPEN/ACKNOWLEDGED ping. */
export async function cancelCommunicationPing(tenantId, referralId, communicationId, uid) {
  if (!uid) throw new Error('cancelCommunicationPing: uid is required');
  const db = getDb();
  await updateDoc(doc(db, PATHS.communication(tenantId, referralId, communicationId)), {
    status: COMMUNICATION_STATUS.CANCELLED,
    cancelledAt: serverTimestamp(),
    cancelledByUid: uid,
  });
}

/**
 * Resolve a ping. Bounded: intended for the SENDER (or admin) once the related
 * referral/rework action is complete. A recipient does NOT resolve by replying.
 * Not exposed as a generic UI button in v1 — kept for the action-complete flow.
 */
export async function resolveCommunicationPing(tenantId, referralId, communicationId, uid) {
  if (!uid) throw new Error('resolveCommunicationPing: uid is required');
  const db = getDb();
  await updateDoc(doc(db, PATHS.communication(tenantId, referralId, communicationId)), {
    status: COMMUNICATION_STATUS.RESOLVED,
    resolvedAt: serverTimestamp(),
    resolvedByUid: uid,
  });
}

// ── Unread / read state ───────────────────────────────────────────────────────

/**
 * Mark a ping read for the current participant. Writes ONLY that participant's
 * own read timestamp (senderLastReadAt or recipientLastReadAt). Never touches the
 * other party's read field or lastMessage* (those are CF-maintained).
 *
 * @param {'sender'|'recipient'} side  which participant the current user is
 */
export async function markPingRead(tenantId, referralId, communicationId, uid, side) {
  if (!uid) throw new Error('markPingRead: uid is required');
  if (side !== 'sender' && side !== 'recipient') {
    throw new Error(`markPingRead: invalid side "${side}"`);
  }
  const db = getDb();
  const field = side === 'sender' ? 'senderLastReadAt' : 'recipientLastReadAt';
  await updateDoc(doc(db, PATHS.communication(tenantId, referralId, communicationId)), {
    [field]: serverTimestamp(),
  });
}

/**
 * Pure, client-computed unread test for a ping (no Firestore reads).
 * A ping is unread for `uid` when the last activity was caused by someone else
 * and happened after this user last read the thread.
 * Legacy pings missing fields: fall back to createdAt for activity, null for reads.
 */
export function isPingUnread(ping, uid) {
  if (!ping || !uid) return false;
  const myRead = ping.senderUid === uid ? ping.senderLastReadAt : ping.recipientLastReadAt;
  const activity = ping.lastMessageAt ?? ping.createdAt ?? null;
  if (!activity) return false;
  if (ping.lastMessageByUid && ping.lastMessageByUid === uid) return false; // own activity
  if (myRead == null) return true;                                          // never read
  return tsMillis(activity) > tsMillis(myRead);
}

// Normalize Firestore Timestamp | Date | epoch-ish to milliseconds for comparison.
function tsMillis(ts) {
  if (!ts) return 0;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (typeof ts.seconds === 'number') return ts.seconds * 1000;
  if (ts instanceof Date) return ts.getTime();
  const n = Date.parse(ts);
  return Number.isNaN(n) ? 0 : n;
}

// ── Reads (collection-group, tenant-scoped) ───────────────────────────────────

function mapDoc(d) {
  return { id: d.id, _refPath: d.ref.parent.parent?.id ?? null, ...d.data() };
}

/** Pings where the user is the recipient (newest first). */
export async function listPingsForRecipient(tenantId, uid, max = 100) {
  if (!tenantId || !uid) return [];
  const db = getDb();
  const q = query(
    collectionGroup(db, 'communications'),
    where('tenantId', '==', tenantId),
    where('recipientUid', '==', uid),
    orderBy('createdAt', 'desc'),
    limit(max),
  );
  const snap = await getDocs(q);
  return snap.docs.map(mapDoc);
}

/** Pings the user sent (newest first). */
export async function listPingsForSender(tenantId, uid, max = 100) {
  if (!tenantId || !uid) return [];
  const db = getDb();
  const q = query(
    collectionGroup(db, 'communications'),
    where('tenantId', '==', tenantId),
    where('senderUid', '==', uid),
    orderBy('createdAt', 'desc'),
    limit(max),
  );
  const snap = await getDocs(q);
  return snap.docs.map(mapDoc);
}

// ── Replies (append-only message subcollection) ───────────────────────────────

/**
 * Build a ping reply (message) document. Throws on invalid input or detected PHI.
 * Returns ONLY whitelisted keys. messageType is always USER_REPLY (client v1).
 */
export function buildCommunicationMessageDoc({
  tenantId,
  referralId,
  communicationId,
  messageId,
  authorUid,
  authorRole,
  body,
  now,
}) {
  if (!tenantId) throw new Error('buildCommunicationMessageDoc: tenantId is required');
  if (!referralId) throw new Error('buildCommunicationMessageDoc: referralId is required');
  if (!communicationId) throw new Error('buildCommunicationMessageDoc: communicationId is required');
  if (!messageId) throw new Error('buildCommunicationMessageDoc: messageId is required');
  if (!authorUid) throw new Error('buildCommunicationMessageDoc: authorUid is required');
  if (!authorRole) throw new Error('buildCommunicationMessageDoc: authorRole is required');

  const trimmed = String(body ?? '').trim();
  if (trimmed === '') throw new Error('Reply cannot be empty.');
  if (trimmed.length > MESSAGE_REPLY_MAX_LENGTH) {
    throw new Error(`Reply is too long (max ${MESSAGE_REPLY_MAX_LENGTH} characters).`);
  }
  const scan = scanForPhi(trimmed);
  if (scan.detected && hasBlockingReasons(scan.reasons)) {
    throw new Error(PHI_BLOCK_MESSAGE);
  }

  const docData = {
    tenantId,
    referralId,
    communicationId,
    messageId,
    authorUid,
    authorRole,
    messageType: MESSAGE_TYPE.USER_REPLY,
    body: trimmed,
    createdAt: now,
  };

  // Defensive: strip any key not in the whitelist (guarantees no PHI/unknown fields).
  for (const k of Object.keys(docData)) {
    if (!MESSAGE_ALLOWED_KEYS.includes(k)) delete docData[k];
  }
  return docData;
}

/**
 * Post a reply to a ping thread (append-only, single create).
 *
 * Auto-acknowledge: if the author is the recipient and the parent ping is OPEN,
 * the parent is acknowledged AFTER the reply commits — a best-effort second write.
 * If that ack fails the reply is NOT rolled back (the message is the durable action).
 *
 * @param {string} tenantId
 * @param {string} referralId
 * @param {string} communicationId
 * @param {{ authorUid, authorRole, body, parentStatus?, parentRecipientUid? }} params
 * @returns {Promise<{ messageId: string, autoAcknowledged: boolean }>}
 */
export async function postCommunicationReply(tenantId, referralId, communicationId, params) {
  if (!tenantId || !referralId || !communicationId) {
    throw new Error('postCommunicationReply: tenantId, referralId, communicationId are required');
  }
  const { authorUid, authorRole, body, parentStatus, parentRecipientUid } = params || {};
  const db = getDb();
  const messageId = generateMessageId();
  const docData = buildCommunicationMessageDoc({
    tenantId,
    referralId,
    communicationId,
    messageId,
    authorUid,
    authorRole,
    body,
    now: serverTimestamp(),
  });

  // Primary durable action: create the reply.
  await setDoc(doc(db, PATHS.communicationMessage(tenantId, referralId, communicationId, messageId)), docData);

  // Best-effort auto-acknowledge: recipient replied to an OPEN ping.
  let autoAcknowledged = false;
  if (parentStatus === COMMUNICATION_STATUS.OPEN && parentRecipientUid && parentRecipientUid === authorUid) {
    try {
      await acknowledgeCommunicationPing(tenantId, referralId, communicationId, authorUid);
      autoAcknowledged = true;
    } catch (err) {
      // Do NOT roll back the reply — it is the primary action.
      console.warn('[postCommunicationReply] auto-acknowledge failed (reply still posted):', err?.message || err);
    }
  }

  return { messageId, autoAcknowledged };
}

/** List replies for a ping thread in chronological order (oldest first). */
export async function listCommunicationMessages(tenantId, referralId, communicationId, max = 200) {
  if (!tenantId || !referralId || !communicationId) return [];
  const db = getDb();
  const q = query(
    collection(db, PATHS.col.communicationMessages(tenantId, referralId, communicationId)),
    orderBy('createdAt', 'asc'),
    limit(max),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// ── Live subscriptions (onSnapshot) ───────────────────────────────────────────
// Each returns an unsubscribe function. Read-only; no new writes/schema.

/** Live: replies for a ping thread, chronological (oldest first). */
export function subscribeToCommunicationMessages(tenantId, referralId, communicationId, { onData, onError } = {}, max = 200) {
  if (!tenantId || !referralId || !communicationId) { onData?.([]); return () => {}; }
  const db = getDb();
  const q = query(
    collection(db, PATHS.col.communicationMessages(tenantId, referralId, communicationId)),
    orderBy('createdAt', 'asc'),
    limit(max),
  );
  return onSnapshot(
    q,
    (snap) => onData?.(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err) => onError?.(err),
  );
}

/** Live: pings where the user is the recipient (newest first). */
export function subscribeToPingsForRecipient(tenantId, uid, { onData, onError } = {}, max = 100) {
  if (!tenantId || !uid) { onData?.([]); return () => {}; }
  const db = getDb();
  const q = query(
    collectionGroup(db, 'communications'),
    where('tenantId', '==', tenantId),
    where('recipientUid', '==', uid),
    orderBy('createdAt', 'desc'),
    limit(max),
  );
  return onSnapshot(q, (snap) => onData?.(snap.docs.map(mapDoc)), (err) => onError?.(err));
}

/** Live: pings the user sent (newest first). */
export function subscribeToPingsForSender(tenantId, uid, { onData, onError } = {}, max = 100) {
  if (!tenantId || !uid) { onData?.([]); return () => {}; }
  const db = getDb();
  const q = query(
    collectionGroup(db, 'communications'),
    where('tenantId', '==', tenantId),
    where('senderUid', '==', uid),
    orderBy('createdAt', 'desc'),
    limit(max),
  );
  return onSnapshot(q, (snap) => onData?.(snap.docs.map(mapDoc)), (err) => onError?.(err));
}
